import {
    GameStat,
    useEndGameMutation,
    useSavePenaltyMutation,
    useSaveProgressMutation,
    useSaveStatsMutation,
    useSaveTeamNameMutation,
    useSetSnowMutation,
    useStartGameMutation,
} from '../gql-types';
import { ProfileContext } from '../contexts/ProfileContext';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTime } from './useTime';

export const useGraphQL = () => {
    const { availableGame, setAvailableGame } = useContext(ProfileContext);
    const { getTimeSplit } = useTime();
    const [saveTeamNameMutationCall] = useSaveTeamNameMutation();
    const [setSnowMutationCall] = useSetSnowMutation();
    const [startGameMutationCall] = useStartGameMutation();
    const [endGameMutationCall] = useEndGameMutation();
    const [saveProgressMutationCall] = useSaveProgressMutation();
    const [saveStatsMutationCall] = useSaveStatsMutation();
    const [savePenaltyMutationCall] = useSavePenaltyMutation();
    const history = useHistory();

    const saveTeamName = (code: string, token: string | null) => {
        if (availableGame) {
            saveTeamNameMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    teamName: code,
                    token,
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.checkSaveTeamName);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const startGame = (token: string | null) => {
        if (availableGame) {
            startGameMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    started: Math.floor(new Date().getTime() / 1000).toString(),
                    token,
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const endGame = (token: string | null) => {
        if (availableGame) {
            const ended = Math.floor(new Date().getTime() / 1000).toString();
            const time = parseInt(ended) - parseInt(availableGame.started ?? '0');
            const penalties = (availableGame.penalties || 0) * 60;
            const { getSeconds, getMinutes, getHours } = getTimeSplit(time + penalties);

            endGameMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    ended: ended,
                    token,
                    finalTime: getHours + ':' + getMinutes + ':' + getSeconds,
                    gameType: availableGame.gameType!,
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const saveProgress = (currentStep: number, stats: GameStat, token: string | null) => {
        if (availableGame) {
            saveProgressMutationCall({
                variables: {
                    _id: availableGame._id!,
                    lastStep: currentStep,
                    code: availableGame.code!,
                    token,
                    stats: {
                        ...availableGame.stats,
                        ['_' + currentStep]: stats,
                    },
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const saveStats = (currentStep: number, stats: GameStat, token: string | null) => {
        if (availableGame) {
            saveStatsMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    token,
                    stats: {
                        ...availableGame.stats,
                        ['_' + currentStep]: stats,
                    },
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const savePenalty = (plusTime: number, token: string | null) => {
        if (availableGame) {
            savePenaltyMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    penalties: plusTime,
                    token,
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };

    const setSnow = (snow: boolean, token: string | null) => {
        if (availableGame) {
            setSnowMutationCall({
                variables: {
                    _id: availableGame._id!,
                    code: availableGame.code!,
                    snow: snow,
                    token,
                },
            })
                .then((data) => {
                    setAvailableGame(data.data?.editGame);
                })
                .catch((err) => {
                    if (err && err?.message === 'UNAUTHORIZED') {
                        history.push({
                            pathname: '/game-in-progress',
                            state: { gameCode: availableGame.code },
                        });
                    } else {
                        throw new Error(err);
                    }
                });
        }
    };
    return { startGame, saveProgress, saveTeamName, savePenalty, endGame, setSnow, saveStats };
};
